import React, { useState, useEffect } from 'react'
import { useAuth } from '../context/AuthContext'
import { useTheme } from '../context/ThemeContext'
import { formatDate } from '../utils/dateFormat'
import { getApiBaseUrl } from '../utils/apiConfig'
import axios from 'axios'

const API_BASE_URL = getApiBaseUrl()

export default function Reconciliation() {
  const { user } = useAuth()
  const { isDark } = useTheme()
  const [loading, setLoading] = useState(false)
  const [reconciliationData, setReconciliationData] = useState(null)
  const [asOfDate, setAsOfDate] = useState(new Date().toISOString().split('T')[0])
  const [autoFixing, setAutoFixing] = useState(false)

  useEffect(() => {
    fetchReconciliationData()
  }, [asOfDate])

  const fetchReconciliationData = async () => {
    try {
      setLoading(true)
      const token = localStorage.getItem('token')
      const response = await axios.get(`${API_BASE_URL}/reconciliation/summary`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { as_of_date: asOfDate }
      })
      setReconciliationData(response.data.data)
    } catch (error) {
      console.error('Failed to fetch reconciliation data:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleAutoFix = async (fixTypes) => {
    try {
      setAutoFixing(true)
      const token = localStorage.getItem('token')
      const response = await axios.post(`${API_BASE_URL}/reconciliation/auto-fix`,
        { fixes: fixTypes },
        { headers: { Authorization: `Bearer ${token}` } }
      )

      // Refresh data after auto-fix
      await fetchReconciliationData()
      alert('Auto-fix completed successfully!')
    } catch (error) {
      console.error('Auto-fix failed:', error)
      alert('Auto-fix failed: ' + (error.response?.data?.message || error.message))
    } finally {
      setAutoFixing(false)
    }
  }

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'bg-red-100 text-red-800 border-red-200'
      case 'high': return 'bg-orange-100 text-orange-800 border-orange-200'
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200'
      case 'low': return 'bg-blue-100 text-blue-800 border-blue-200'
      default: return 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }

  const getHealthStatusColor = (status) => {
    switch (status) {
      case 'Healthy': return 'text-green-600'
      case 'Needs Attention': return 'text-red-600'
      default: return 'text-gray-600'
    }
  }

  const bgColor = isDark ? 'bg-slate-900' : 'bg-white'
  const textColor = isDark ? 'text-white' : 'text-gray-800'
  const borderColor = isDark ? 'border-slate-700' : 'border-gray-200'
  const cardBgColor = isDark ? 'bg-slate-800' : 'bg-gray-50'

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className={`text-2xl font-bold ${textColor}`}>🔄 Bank Reconciliation</h2>
          <p className={`mt-1 text-sm ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
            Match bank statements with internal records and identify discrepancies
          </p>
        </div>

        <div className="flex items-center gap-4">
          <div>
            <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
              As of Date
            </label>
            <input
              type="date"
              value={asOfDate}
              onChange={(e) => setAsOfDate(e.target.value)}
              className={`rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-700'
              }`}
            />
          </div>

          <button
            onClick={() => handleAutoFix(['post_missing_entries', 'remove_orphan_entries', 'repair_incomplete_entries'])}
            disabled={autoFixing || loading}
            className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition disabled:opacity-50 mt-6"
          >
            {autoFixing ? '🔄 Fixing...' : '🔧 Auto-Fix Issues'}
          </button>
        </div>
      </div>

      {loading ? (
        <div className={`rounded-lg p-8 text-center ${bgColor}`}>
          <div className="text-4xl mb-4">⏳</div>
          <p className={isDark ? 'text-slate-300' : 'text-gray-600'}>Loading reconciliation data...</p>
        </div>
      ) : reconciliationData ? (
        <>
          {/* Overall Health Status */}
          <div className={`${cardBgColor} rounded-lg p-6 border ${borderColor}`}>
            <div className="flex items-center justify-between">
              <div>
                <h3 className={`text-lg font-semibold ${textColor}`}>Overall System Health</h3>
                <p className={`mt-1 text-sm ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
                  Report generated on {formatDate(reconciliationData.generated_at)}
                </p>
              </div>
              <div className="text-right">
                <div className={`text-3xl font-bold ${getHealthStatusColor(reconciliationData.validations?.overall_status)}`}>
                  {reconciliationData.validations?.overall_status || 'Unknown'}
                </div>
                <div className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
                  {reconciliationData.validations?.total_discrepancies || 0} discrepancies found
                </div>
              </div>
            </div>

            {(reconciliationData.validations?.critical_issues > 0 || reconciliationData.validations?.high_priority_issues > 0) && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-800">
                  ⚠️ Action Required: {reconciliationData.validations.critical_issues} critical and {reconciliationData.validations.high_priority_issues} high-priority issues need attention.
                </p>
              </div>
            )}
          </div>

          {/* Double-Entry Accounting Validation */}
          <div className={`${cardBgColor} rounded-lg p-6 border ${borderColor}`}>
            <h3 className={`text-lg font-semibold ${textColor} mb-4`}>📝 Double-Entry Accounting</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  RM {parseFloat(reconciliationData.summary?.double_entry_accounting?.total_debits || 0).toFixed(2)}
                </div>
                <div className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>Total Debits</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-red-600">
                  RM {parseFloat(reconciliationData.summary?.double_entry_accounting?.total_credits || 0).toFixed(2)}
                </div>
                <div className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>Total Credits</div>
              </div>
              <div className="text-center">
                <div className={`text-2xl font-bold ${reconciliationData.summary?.double_entry_accounting?.balanced ? 'text-green-600' : 'text-red-600'}`}>
                  {reconciliationData.summary?.double_entry_accounting?.balanced ? '✓ Balanced' : '✗ Unbalanced'}
                </div>
                <div className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>Status</div>
              </div>
              <div className="text-center">
                <div className={`text-2xl font-bold ${parseFloat(reconciliationData.summary?.double_entry_accounting?.difference || 0) > 0.01 ? 'text-red-600' : 'text-green-600'}`}>
                  RM {parseFloat(reconciliationData.summary?.double_entry_accounting?.difference || 0).toFixed(2)}
                </div>
                <div className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>Difference</div>
              </div>
            </div>
          </div>

          {/* Document Reconciliation */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Invoices */}
            <div className={`${cardBgColor} rounded-lg p-4 border ${borderColor}`}>
              <div className="flex items-center justify-between mb-3">
                <h4 className={`font-medium ${textColor}`}>📄 Invoices</h4>
                <span className={`px-2 py-1 rounded text-xs font-medium ${
                  reconciliationData.summary?.outgoing_invoices?.reconciled ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  {reconciliationData.summary?.outgoing_invoices?.reconciled ? '✓ Reconciled' : '✗ Mismatch'}
                </span>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>Operational Total:</span>
                  <span className={`text-sm font-medium ${textColor}`}>
                    RM {parseFloat(reconciliationData.summary?.outgoing_invoices?.operational_total || 0).toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>Ledger Total:</span>
                  <span className={`text-sm font-medium ${textColor}`}>
                    RM {parseFloat(reconciliationData.summary?.outgoing_invoices?.ledger_total || 0).toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between border-t pt-2 mt-2">
                  <span className={`text-sm font-medium ${textColor}`}>Difference:</span>
                  <span className={`text-sm font-bold ${parseFloat(reconciliationData.summary?.outgoing_invoices?.difference || 0) > 0.01 ? 'text-red-600' : 'text-green-600'}`}>
                    RM {parseFloat(reconciliationData.summary?.outgoing_invoices?.difference || 0).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>

            {/* Expenses */}
            <div className={`${cardBgColor} rounded-lg p-4 border ${borderColor}`}>
              <div className="flex items-center justify-between mb-3">
                <h4 className={`font-medium ${textColor}`}>💸 Expenses</h4>
                <span className={`px-2 py-1 rounded text-xs font-medium ${
                  reconciliationData.summary?.expenses?.reconciled ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  {reconciliationData.summary?.expenses?.reconciled ? '✓ Reconciled' : '✗ Mismatch'}
                </span>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>Operational Total:</span>
                  <span className={`text-sm font-medium ${textColor}`}>
                    RM {parseFloat(reconciliationData.summary?.expenses?.operational_total || 0).toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>Ledger Total:</span>
                  <span className={`text-sm font-medium ${textColor}`}>
                    RM {parseFloat(reconciliationData.summary?.expenses?.ledger_total || 0).toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between border-t pt-2 mt-2">
                  <span className={`text-sm font-medium ${textColor}`}>Difference:</span>
                  <span className={`text-sm font-bold ${parseFloat(reconciliationData.summary?.expenses?.difference || 0) > 0.01 ? 'text-red-600' : 'text-green-600'}`}>
                    RM {parseFloat(reconciliationData.summary?.expenses?.difference || 0).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>

            {/* Salaries */}
            <div className={`${cardBgColor} rounded-lg p-4 border ${borderColor}`}>
              <div className="flex items-center justify-between mb-3">
                <h4 className={`font-medium ${textColor}`}>💰 Salaries</h4>
                <span className={`px-2 py-1 rounded text-xs font-medium ${
                  reconciliationData.summary?.salaries?.reconciled ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  {reconciliationData.summary?.salaries?.reconciled ? '✓ Reconciled' : '✗ Mismatch'}
                </span>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>Operational Total:</span>
                  <span className={`text-sm font-medium ${textColor}`}>
                    RM {parseFloat(reconciliationData.summary?.salaries?.operational_total || 0).toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>Ledger Total:</span>
                  <span className={`text-sm font-medium ${textColor}`}>
                    RM {parseFloat(reconciliationData.summary?.salaries?.ledger_total || 0).toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between border-t pt-2 mt-2">
                  <span className={`text-sm font-medium ${textColor}`}>Difference:</span>
                  <span className={`text-sm font-bold ${parseFloat(reconciliationData.summary?.salaries?.difference || 0) > 0.01 ? 'text-red-600' : 'text-green-600'}`}>
                    RM {parseFloat(reconciliationData.summary?.salaries?.difference || 0).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Discrepancies */}
          {reconciliationData.discrepancies?.length > 0 && (
            <div className={`${cardBgColor} rounded-lg p-6 border ${borderColor}`}>
              <h3 className={`text-lg font-semibold ${textColor} mb-4`}>⚠️ Identified Discrepancies</h3>
              <div className="space-y-3">
                {reconciliationData.discrepancies.map((discrepancy, index) => (
                  <div key={index} className={`p-4 rounded-lg border ${getSeverityColor(discrepancy.severity)}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-lg font-medium">{discrepancy.type === 'double_entry_violation' ? '🚨' :
                                               discrepancy.type === 'invoice_reconciliation' ? '📄' :
                                               discrepancy.type === 'expense_reconciliation' ? '💸' : '⚠️'}</span>
                          <span className="font-medium">{discrepancy.message}</span>
                        </div>
                        {discrepancy.details && (
                          <div className="mt-2 text-sm opacity-75">
                            {Object.entries(discrepancy.details).map(([key, value]) => (
                              <div key={key} className="flex gap-2">
                                <span className="font-medium">{key}:</span>
                                <span>{value}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      <span className={`px-2 py-1 rounded text-xs font-medium uppercase ${
                        discrepancy.severity === 'critical' ? 'bg-red-500 text-white' :
                        discrepancy.severity === 'high' ? 'bg-orange-500 text-white' :
                        discrepancy.severity === 'medium' ? 'bg-yellow-500 text-white' :
                        'bg-blue-500 text-white'
                      }`}>
                        {discrepancy.severity}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Unposted Documents */}
          {reconciliationData.summary?.unposted_documents?.total > 0 && (
            <div className={`${cardBgColor} rounded-lg p-6 border ${borderColor}`}>
              <h3 className={`text-lg font-semibold ${textColor} mb-4`}>📋 Unposted Documents</h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-orange-600">
                    {reconciliationData.summary.unposted_documents.invoices}
                  </div>
                  <div className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>Invoices</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-orange-600">
                    {reconciliationData.summary.unposted_documents.expenses}
                  </div>
                  <div className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>Expenses</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-orange-600">
                    {reconciliationData.summary.unposted_documents.salaries}
                  </div>
                  <div className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>Salaries</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-red-600">
                    {reconciliationData.summary.unposted_documents.total}
                  </div>
                  <div className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>Total Unposted</div>
                </div>
              </div>
            </div>
          )}

          {/* Warnings */}
          {reconciliationData.warnings?.length > 0 && (
            <div className={`${cardBgColor} rounded-lg p-6 border ${borderColor}`}>
              <h3 className={`text-lg font-semibold ${textColor} mb-4`}>⚡ System Warnings</h3>
              <div className="space-y-2">
                {reconciliationData.warnings.map((warning, index) => (
                  <div key={index} className={`p-3 rounded bg-yellow-50 border border-yellow-200 text-yellow-800 text-sm`}>
                    {warning}
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      ) : (
        <div className={`rounded-lg p-8 text-center ${bgColor}`}>
          <div className="text-4xl mb-4">🔄</div>
          <p className={isDark ? 'text-slate-300' : 'text-gray-600'}>No reconciliation data available</p>
        </div>
      )}
    </div>
  )
}
